import { isUndefined } from 'lodash-es';
import { type KeyboardEventHandler, useRef } from 'react';

import Colors from '@dynatrace/strato-design-tokens/colors';

import { Knob } from './Knob.js';
import { SelectionBackdrop } from './SelectionBackdrop.js';
import type { RelativeAbsolutePosition } from '../../types/pointer-events.js';
import { KNOB_HEIGHT, KNOB_WIDTH } from '../overlays/constants.js';
import { clampSelectionArea } from './utils/clamp-selection-area.js';
import { usePoliteContainer } from '../../../../overlays/hooks/usePoliteContainer.js';
import { BottomMagnifier } from '../crosshair/components/BottomMagnifier.js';
import { normalizeSelectionArea } from '../interactions-overlay/utils/normalize-selection-area.js';

interface SelectionProps {
  bounds: DOMRect;
  focusable: boolean;
  interactable: boolean;
  formatter: (value: number) => string;
  startX: number;
  endX: number;
  height: number;
  width: number;
  handleSelectionAreaKeyDown: KeyboardEventHandler;
  handleSelectionAreaStartKeyDown: KeyboardEventHandler;
  handleSelectionAreaEndKeyDown: KeyboardEventHandler;
  start: RelativeAbsolutePosition;
  end: RelativeAbsolutePosition;
  onSelectionChange: ({
    selection,
    accessible,
  }: {
    selection: [RelativeAbsolutePosition, RelativeAbsolutePosition];
    accessible: boolean;
  }) => void;
}

const extendX = (relativeAbsolute: RelativeAbsolutePosition, x: number) => ({
  ...relativeAbsolute,
  absolute: {
    ...relativeAbsolute.absolute,
    x: relativeAbsolute.absolute.x + x,
  },
  relative: {
    ...relativeAbsolute.relative,
    x: relativeAbsolute.relative.x + x,
  },
});

export const Selection = (props: SelectionProps) => {
  const {
    startX,
    endX,
    height,
    width,
    focusable,
    interactable,
    formatter,
    bounds,
    handleSelectionAreaKeyDown,
    handleSelectionAreaStartKeyDown,
    handleSelectionAreaEndKeyDown,
    start,
    end,
    onSelectionChange,
  } = props;
  const range: [number, number] = [0, width];
  /**
   * Stores the selection edge that is NOT being adjusted during selection adjustment and acts as pivot
   */
  const selectionEdgePivotRef = useRef<RelativeAbsolutePosition>(undefined);

  const onDragMove = (x: number) => {
    // Adjust selection edges if dragging from one of the edges
    if (!isUndefined(selectionEdgePivotRef.current)) {
      const position = {
        relative: {
          x: x - bounds.left,
          y: height / 2,
        },
        absolute: {
          y: height / 2 + bounds.top,
          x: x,
        },
      };

      const nextSelection = clampSelectionArea(
        normalizeSelectionArea([position, selectionEdgePivotRef.current]),
        range,
      );
      onSelectionChange?.({ selection: nextSelection, accessible: false });
    }
  };

  const onWholeSelectionMove = (x: number) => {
    const newSelectionAreaStart = extendX(start, x);
    const newSelectionAreaEnd = extendX(end, x);

    const nextSelection = clampSelectionArea(
      normalizeSelectionArea([newSelectionAreaStart, newSelectionAreaEnd]),
      range,
    );
    onSelectionChange?.({ selection: nextSelection, accessible: true });
  };

  const Portal = usePoliteContainer();

  return (
    <>
      <SelectionBackdrop
        focusable={focusable}
        startX={startX}
        endX={endX}
        height={height}
        isSynchronizedSelection={false}
        interactable={interactable}
        onDragMove={onWholeSelectionMove}
        handleSelectionAreaKeyDown={handleSelectionAreaKeyDown}
      />
      <Portal>
        <BottomMagnifier
          label={formatter(startX)}
          mouseX={startX}
          offset={0}
          color={Colors.Border.Primary.Accent}
          chartBounds={bounds}
        />
      </Portal>
      <Knob
        y={height / 2 - KNOB_HEIGHT / 2}
        x={startX - KNOB_WIDTH / 2}
        interactable={interactable}
        focusable={focusable}
        onKeyDown={handleSelectionAreaStartKeyDown}
        onDragMove={onDragMove}
        onDragStart={() => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          selectionEdgePivotRef!.current! = end;
        }}
        onDragEnd={() => (selectionEdgePivotRef.current = undefined)}
      />

      <Portal>
        <BottomMagnifier
          label={formatter(endX)}
          mouseX={endX}
          offset={0}
          color={Colors.Border.Primary.Accent}
          chartBounds={bounds}
        />
      </Portal>
      <Knob
        y={height / 2 - KNOB_HEIGHT / 2}
        x={endX - KNOB_WIDTH / 2}
        interactable={interactable}
        focusable={focusable}
        onKeyDown={handleSelectionAreaEndKeyDown}
        onDragMove={onDragMove}
        onDragStart={() => (selectionEdgePivotRef.current = start)}
        onDragEnd={() => (selectionEdgePivotRef.current = undefined)}
      />
    </>
  );
};
